/*
  mountainFLOW: playback for the multicolumn video columns.

  Two modes, deliberately separated:

  - Preview. Pointer devices start a silent, looping preview on hover, and the
    keyboard gets the same thing on focus. It is decorative, so it is skipped
    entirely under prefers-reduced-motion.
  - Playback. A labelled button (or a click on the poster) restarts the video with
    sound and hands over to the browser's native controls, which are keyboard
    operable, screen-reader labelled and carry the captions menu.

  Audio only ever starts from a real activation. That is WCAG 1.4.2 (no auto audio),
  and it is also the only thing that works: browsers reject unmuted play() that was
  not triggered by a gesture, and a mouseenter does not count.
*/
(() => {
  const SELECTOR = '[data-multicolumn-video]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Sound is exclusive -- starting one column stops whichever was talking.
  let audible = null;

  class MulticolumnVideo {
    constructor(root) {
      this.root = root;
      this.media = root.querySelector('video-media');
      this.toggle = root.querySelector('[data-multicolumn-video-toggle]');
      this.surface = root.querySelector('[data-multicolumn-video-surface]');
      this.labels = {
        play: root.dataset.labelPlay || 'Play with sound',
        pause: root.dataset.labelPause || 'Pause',
        resume: root.dataset.labelResume || 'Resume'
      };
      this.previewOnHover = root.dataset.previewOnHover === 'true';
      this.video = null;

      if (!this.media || !this.toggle) return;

      this.setState('idle');
      this.bind();
    }

    bind() {
      this.toggle.addEventListener('click', () => this.onToggle());
      this.surface?.addEventListener('click', () => this.activate());

      if (this.previewOnHover) {
        this.root.addEventListener('mouseenter', () => this.startPreview());
        this.root.addEventListener('mouseleave', () => this.stopPreview());
        this.root.addEventListener('focusin', () => this.startPreview());
        this.root.addEventListener('focusout', (event) => {
          if (!this.root.contains(event.relatedTarget)) this.stopPreview();
        });
      }

      this.root.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isActive) this.stop();
      });

      // A video that scrolls away should not keep talking to an empty room.
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (entries[entries.length - 1].isIntersecting) return;
          if (this.isActive) this.stop();
          else this.stopPreview();
        }, { threshold: 0 });
        this.observer.observe(this.root);
      }
    }

    get isActive() {
      return audible === this;
    }

    /*
      The theme's <video-media> keeps the <video> in a <template> until the first
      play() call, which swaps it in synchronously. Asking it to play is therefore
      also how the element gets built.
    */
    ensureVideo() {
      if (this.video?.isConnected) return this.video;

      this.video = this.media.querySelector('video');
      if (!this.video) {
        this.media.play();
        this.video = this.media.querySelector('video');
      }

      if (this.video && !this.video.dataset.mfBound) {
        this.video.dataset.mfBound = '1';
        this.addCaptions(this.video);
        this.video.addEventListener('ended', () => this.stop());
        this.video.addEventListener('play', () => {
          if (this.isActive) this.setState('playing');
        });
        this.video.addEventListener('pause', () => {
          if (this.isActive && !this.video.seeking) this.setState('paused');
        });
      }

      return this.video;
    }

    /*
      Shopify's video_tag has no way to emit a <track>, so the caption file goes on
      the wrapper and is attached once the <video> exists.
    */
    addCaptions(video) {
      const src = this.root.dataset.captionsSrc;
      if (!src || video.querySelector('track')) return;

      const track = document.createElement('track');
      track.kind = 'captions';
      track.src = src;
      track.srclang = this.root.dataset.captionsLang || 'en';
      track.label = this.root.dataset.captionsLabel || 'English';
      track.default = true;
      video.appendChild(track);
    }

    setState(state) {
      this.root.dataset.state = state;

      const label = state === 'playing'
        ? this.labels.pause
        : state === 'paused' ? this.labels.resume : this.labels.play;

      this.toggle.setAttribute('aria-label', `${label}: ${this.root.dataset.videoTitle || ''}`.trim().replace(/:$/, ''));
      this.toggle.setAttribute('aria-pressed', state === 'playing' ? 'true' : 'false');
    }

    /* ---- preview ---------------------------------------------------------- */

    startPreview() {
      if (this.isActive || reducedMotion.matches) return;

      const video = this.ensureVideo();
      if (!video) return;

      video.muted = true;
      video.loop = true;
      video.controls = false;
      this.setState('preview');
      video.play().catch(() => {});
    }

    stopPreview() {
      if (this.isActive || this.root.dataset.state !== 'preview') return;

      this.video?.pause();
      this.setState('idle');
    }

    /* ---- playback --------------------------------------------------------- */

    onToggle() {
      if (!this.isActive) return this.activate();

      if (this.video?.paused) {
        this.video.play().catch(() => {});
      } else {
        this.video?.pause();
      }
    }

    activate() {
      const video = this.ensureVideo();
      if (!video) return;

      if (audible && audible !== this) audible.stop();
      audible = this;

      video.muted = false;
      video.loop = false;
      video.controls = true;
      video.currentTime = 0;
      this.setState('playing');

      video.play().catch((error) => {
        // Pausing -- or another column starting -- while play() is still pending
        // rejects the promise with AbortError. That is the outcome we asked for, so
        // tearing the column down here would undo the pause we just performed.
        if (error?.name === 'AbortError' || !this.isActive) return;

        if (error.name === 'NotAllowedError') {
          // Autoplay policy can still refuse an unmuted start on a cold profile.
          // Fall back to a silent video that keeps its controls, so the visitor can
          // unmute it themselves, rather than to nothing at all.
          video.muted = true;
          video.play().catch(() => this.setState('paused'));
        } else {
          this.stop();
        }
      });
    }

    stop() {
      const video = this.video;
      if (audible === this) audible = null;

      if (video) {
        video.pause();
        video.controls = false;
        video.muted = true;
        video.loop = true;
      }

      this.setState('idle');
    }
  }

  const init = (root) => {
    (root || document).querySelectorAll(SELECTOR).forEach((el) => {
      if (el.dataset.mfVideoBound) return;
      el.dataset.mfVideoBound = '1';
      new MulticolumnVideo(el);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // the theme editor replaces section markup in place
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
